// 207B §3 — THE ONE CHAT TIME. `fmtRelTime` moved here from
// src/views/ChatView.tsx UNCHANGED (rule 3, ca7d7c59 — ChatView.tsx:371-401@4239df64),
// so a recording can run the function every chat time goes through (the
// General chat page, the sidebar and the column, both pop-outs, the
// Dashboard's Personal Messages); then 207B §3 split it into its two parts and
// ONE renderer (below). ChatView imports it and re-exports it, so the
// Dashboard reads it where it did. The `any`s and eslint warnings the move
// carried were re-recorded as a TRANSFER (190 C.4's method).
import React from "react"; // 207B §3 — the renderer draws JSX
import type { TFn } from "./taxonomyTypes"; // 206E §3 — the translator a shared builder is handed
import { localDayStr } from "./localToday"; // 207B §3 — a chat row's local day

/* 206E §3 — ONE RELATIVE MESSAGE TIME, MOVED UNCHANGED from Dashboard.tsx's
   `fmtTime2` (its Personal Messages), which already spoke through the
   dictionary (`dash.time.*`: "just now", "5m ago", "3h ago", "Yesterday
   14:05"), while the General chat page's own `fmtT` — the same algorithm,
   line for line — printed those words in English. The host passes its
   translator (`useTranslation().t`, `any`-typed at the source — taken as
   `unknown` and read as the tree's own `TFn`, a compile-time cast). */
/* 207B §3 [RULED — Laurent: "put the hours "(HH:MM)" in italics to be more
   distinct from the date"] — the clock, brackets included, prints in ITALICS
   after the relative time: « Hier *(09:12)* ». So 207 §9's time comes in its
   two parts — `relTimeParts`, the body above with the halves no longer joined —
   and ONE renderer prints them at every chat time, `chatTime`; `ChatClock` is
   the clock alone, which the case timeline's chat rows print after their date.
   The italics are one class, `.chat-clock` (src/App.css). */
const clockOf = (d: Date) => `(${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")})`;
export const relTimeParts = (ts: any, translate: unknown): { rel: string; clock: string } => {
  const tr = translate as TFn;
  const d = new Date(ts);
  const now2 = new Date();
  /* 207 §9 [RULED — Laurent: "no time indication, pls add it but just between
     brackets '(HH:MM)' is fine"] — EVERY chat time ends with the message's
     clock time: « il y a 5m (14:00) », « Hier (09:12) », « 16/09 (09:12) ». The
     clock was already printed for yesterday and older, without the brackets. */
  const clock = clockOf(d);
  const diffMin = Math.floor(((now2 as any) - (d as any)) / 60000);
  if (diffMin < 1) return { rel: tr("dash.time.just_now"), clock };
  if (diffMin < 60) return { rel: tr("dash.time.m_ago", { n: diffMin }), clock };
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24 && d.getDate() === now2.getDate())
    return { rel: tr("dash.time.h_ago", { n: diffH }), clock };
  const yest = new Date(now2);
  yest.setDate(yest.getDate() - 1);
  if (
    d.getDate() === yest.getDate() &&
    d.getMonth() === yest.getMonth()
  )
    return { rel: tr("dash.time.yesterday"), clock };
  return { rel: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`, clock };
};

/** 207B §3 — THE chat time: the relative time, then its clock in italics.
    A function, not a component — no hook; its hosts hand it their untyped
    `m.ts` and `t` as they handed `fmtRelTime` (`unknown` in). */
export const chatTime = (ts: unknown, t: unknown) => {
  const { rel, clock } = relTimeParts(ts, t);
  return (
    <>
      {rel} <span className="chat-clock">{clock}</span>
    </>
  );
};

/** 207B §3 — the clock alone, in italics: « (09:12) ». */
export function ChatClock({ ts }: Readonly<{ ts: string | number | Date }>) {
  return <span className="chat-clock">{clockOf(new Date(ts))}</span>;
}

/* 207B §3 [ADVISOR] — A CHAT MESSAGE ON A CASE'S TIMELINE: its LOCAL day —
   `toISOString().slice(0, 10)` filed a message sent at 00:30 in Brussels
   under the day before — and its moment, which puts the clock after the date
   and orders the day's chat rows by time. */
export const chatRowMoment = (ts: string | number | Date) => {
  const d = new Date(ts);
  return { date: localDayStr(d), ts: d.getTime() };
};
interface Dated { date?: string | null; ts?: number }
/** The timeline's order: newest day first; within a day, a row with a moment (a chat message) by its time, newest first, ahead of the day's undated rows. */
export const newestFirst = (a: Dated, b: Dated) =>
  (b.date ?? "").localeCompare(a.date ?? "") || (b.ts ?? 0) - (a.ts ?? 0);
